import { tickNpc } from '@/domain/npc/lifecycle';
import { recordDeath } from '@/domain/lineage/mutate';
import { useStore } from '@/infra/store';
import { eventBus } from '@/infra/event-bus/EventBus';
import { logger } from '@/infra/logger/Logger';
import { saveCurrentSnapshot } from './save';
import { getWorldEvolutionService } from './world-evolution';
import './npc-events';

export interface NpcLifeService {
  start(intervalMs?: number): void;
  stop(): void;
  tickNow(nowMs?: number): void;
}

class NpcLifeServiceImpl implements NpcLifeService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastTickAt = Date.now();

  start(intervalMs = 60_000): void {
    if (this.intervalId !== null) return;
    this.lastTickAt = Date.now();
    this.intervalId = setInterval(() => {
      this.tickNow(Date.now());
    }, intervalMs);
    logger.info('npc-life.started');
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  tickNow(nowMs: number = Date.now()): void {
    const dt = nowMs - this.lastTickAt;
    this.lastTickAt = nowMs;
    if (dt <= 0) return;
    const s = useStore.getState();
    const npcs = s.npcs;
    if (npcs.length === 0) return;
    let lineage = s.lineage;
    let changed = false;
    let anyDied = false;
    const next = npcs.map((npc) => {
      if (npc.dead) return npc;
      const r = tickNpc(npc, dt, nowMs);
      if (r.stageChanged && !r.died) {
        eventBus.emit('npc.stage-changed', { npcId: npc.id, from: npc.stage, to: r.npc.stage });
      }
      if (r.died) {
        eventBus.emit('npc.died', { npcId: npc.id, cause: 'old-age', atMs: nowMs });
        lineage = recordDeath(lineage, npc.id, 'old-age', nowMs);
        // U6: cria memorial
        getWorldEvolutionService().onNpcDeath(
          { ...r.npc, dead: true, diedAt: nowMs, causeOfDeath: 'old-age' },
          'old-age',
        );
        anyDied = true;
      }
      if (r.stageChanged || r.died) changed = true;
      return r.npc;
    });
    if (changed) {
      useStore.setState({ npcs: next, lineage });
      if (anyDied) void saveCurrentSnapshot();
    }
  }
}

let _instance: NpcLifeService | null = null;

export function getNpcLifeService(): NpcLifeService {
  if (!_instance) _instance = new NpcLifeServiceImpl();
  return _instance;
}

export function setNpcLifeServiceForTests(impl: NpcLifeService): void {
  _instance = impl;
}

export function resetNpcLifeServiceForTests(): void {
  _instance?.stop();
  _instance = null;
}
